import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDrawer, IconVelocimeter, IconWarning } from '@consumidor-positivo/aurora';

import { useRedline } from '@libs/redline';
import { dateToLocale } from '@libs/utils/helpers/dateToLocale';
import { usePrivatePagesHandlers } from './useHandlers';
import { usePrivatePagesStates } from './useStates';
import { useUserStore } from '@common/store/useUserStore';
import { useUser } from '@common/hooks/useUser';;
import * as partnerLogo from "@dues/helpers/partnerLogo"
import * as magalu from "@dues/helpers/magalu";
import { pageNames } from '@common/routes/pageNames';
import constants from '@common/constants';
import { getCookie } from '@libs/utils/helpers/getSafeCookies';
import { getAnonymousID } from 'redline-client-sdk';

import useAuthenticatedUser from '@libs/acp/hooks/useAuthenticatedUser';

export const usePrivatePageHook = () => {
  const [myDebtStore] = useUserStore((state) => [state.myDebt]);
  const navigate = useNavigate();
  const { track: trackRedline } = useRedline();

  //#region ACP
  const { authenticatedUser, hasPermission, tokenOrigin } = useAuthenticatedUser();
  const {
    auth: { access_token },
    customer,
  } = authenticatedUser;
  const isNCADCheckout = authenticatedUser?.customer?.required_updates?.includes('account_ncad');
  const isNCADGeneral = !isNCADCheckout && tokenOrigin() === 'ncad';

  const { hasFullPermissions, hasPermissionToAccessFullHeader } = useMemo(() => {
    const hasFullPermissions = hasPermission('all');
    const hasPermissionToAccessFullHeader =
      hasFullPermissions ||
      (hasPermission('read_agreements') && hasPermission('read_debts') && hasPermission('renegociate_debts') && hasPermission('reneg'));

    return {
      hasFullPermissions,
      hasPermissionToAccessFullHeader,
    };
  }, [access_token]);
  //#region End ACP

	const { firstName, fullName } = useUser();
  const emailStatus = authenticatedUser.customer.contactStatus.email;
  const { bounced, customer_engagement_status: disabled } = emailStatus;
  const updateEmail = bounced || disabled !== '';

  const defaultNotification = {
    title: 'Você já viu seu Score?',
    Icon: <IconVelocimeter color="info" />,
    onClick: () => navigate(pageNames.myScore.path),
    createdAt: dateToLocale(),
  };

	const updateEmailNotificationMessage = 'Atualize seu e-mail para manter sua conta segura';
  const updateEmailNotification = {
    title: updateEmailNotificationMessage,
    Icon: <IconWarning color="info" />,
    onClick: () => {
      let eventStatus = '';
      const disabledStatus = !!disabled ? `disabled-${disabled}` : '';

      if (bounced && !!disabled) eventStatus = `bounced-${disabledStatus}`;
      else if (bounced) eventStatus = `bounced`;
      else if (!!disabled) eventStatus = disabledStatus;

      trackRedline.userTracking.elementClicked({
        name: `updated-email-${eventStatus}-notification`,
        location: 'notification-bar',
        elementType: 'button',
        text: updateEmailNotificationMessage
      });

      navigate(pageNames.updateEmailACP.path);
    },
    createdAt: dateToLocale(),
  };

  const [recentNotifications, setRecentNotifications] = useState<(typeof defaultNotification)[]>(() => {
    const notification = [defaultNotification];
    if (updateEmail) notification.unshift(updateEmailNotification);

    return notification;
  });


  const partnerImage = partnerLogo.getDebtLogo(myDebtStore)
  const isMagalu = magalu.getShouldUseMagaluBrandDebt(myDebtStore?.partner?.partnerIdentifier, myDebtStore);

  const { dataSourceNavbar, dataSourceNavbarVertical, showLogoutModal, setShowLogoutModal } = usePrivatePagesStates();
  const { onClickBackMenu, onClickLogout, onClickNotLogout, onClickLogoutModal } = usePrivatePagesHandlers({ setShowLogoutModal });
  const { drawerOpen, handleOpenDrawer } = useDrawer({
    menu: false,
    notification: false,
  });

  useEffect(() => {
    if (updateEmail) {
      setRecentNotifications([updateEmailNotification, defaultNotification]);
    } else {
      setRecentNotifications([defaultNotification]);
    }
  }, [updateEmail]);

  useEffect(() => {
    if (drawerOpen.notification && updateEmail) {
      const event = new CustomEvent(constants.customEvents.EmailBouncedCTAViewed, {
        detail: {
          cardId: 'notification',
          emailStatus: emailStatus,
        },
      });
      window.dispatchEvent(event);
    }
  }, [drawerOpen, updateEmail]);

  const handleOpenNotification = () => {
    trackRedline.userTracking.elementClicked({
      elementType: 'button',
      name: 'notifications-menu',
      location: window?.location?.pathname,
    });
    if (!fakeDoorCookie) setShowNotificationFakeDoor(true);
    handleOpenDrawer('notification');
  };

  const fakeDoorCookie = getCookie('fd_notification_id');
  const [showNotificationFakeDoor, setShowNotificationFakeDoor] = useState(!fakeDoorCookie || fakeDoorCookie !== getAnonymousID());
  const [showNotificationIcon, setShowNotificationIcon] = useState(!fakeDoorCookie || fakeDoorCookie !== getAnonymousID());

  useEffect(() => {
    if (!fakeDoorCookie) setShowNotificationFakeDoor(true);
  }, [window.location.pathname]);

  return {
    dataSourceNavbar,
    dataSourceNavbarVertical,
    drawerOpen,
    handleOpenDrawer,
    onClickBackMenu,
    onClickLogout,
    onClickNotLogout,
    onClickLogoutModal,
    partnerImage,
    showLogoutModal,
    firstName,
    fullName: fullName || customer.full_name,
    recentNotifications,
    handleOpenNotification,
    showNotificationFakeDoor,
    setShowNotificationFakeDoor,
    showNotificationIcon,
    setShowNotificationIcon,
    isNCADCheckout,
    isNCADGeneral,
    hasFullPermissions,
    hasPermissionToAccessFullHeader,
    isMagalu
  };
};
